import { Button, Table, Text } from "@uhg-harmony/react";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import styled, { css } from "styled-components";
import { createLogger } from "../../../utils/logger";
import { fetchUserNotifications } from "@uhg-ecp/ecp-notifications-sc-subscriber";
import { getToken } from "../../../config/getToken";
import { withRequestDeadline } from "../../../utils/requestDeadline";

const logger = createLogger("NotificationData");

interface NotificationItem {
  id: number;
  recipientId?: string;
  value?: string;
  createdAt?: string;
  title?: string;
  priority?: string;
  category?: string;
  isRead: string;
  message?: string;
  publisherName?: string;
  timestamp?: string;
  requestEpoch?: number;
  requestId?: string;
  [key: string]: unknown;
}

interface NotificationDataProps {
  env?: string;
  initialDays?: number;
}

function NotificationData({
  env: envProp,
  initialDays: initialDaysProp,
}: NotificationDataProps) {
  const [searchParams] = useSearchParams();

  const env = searchParams.get("env") || envProp || "dev";
  const initialDays =
    parseInt(searchParams.get("initialDays") ?? "") || initialDaysProp || 30;
  const pageSize = parseInt(searchParams.get("pageSize") ?? "") || 10;

  const [fetchError, setFetchError] = useState<string | null>(null);
  const [stickyDataSource, setStickyDataSource] = useState<NotificationItem[]>(
    [],
  );

  useEffect(() => {
    if (!env) return;

    let cancelled = false;

    const loadNotifications = async () => {
      try {
        const token = await getToken();
        if (!token || cancelled) return;

        const inputRequest = {
          lookbackDays: initialDays,
          limit: 25,
          filter: null,
        };

        const selectedFields: string[] = [];

        const rsp = await withRequestDeadline(() =>
          fetchUserNotifications(token, env, inputRequest, selectedFields),
        );

        if (cancelled) return;

        if (rsp?.success) {
          const items: NotificationItem[] = (
            (rsp?.data?.data?.userNotifications?.items ?? []) as Record<
              string,
              unknown
            >[]
          ).map((item, idx: number) => ({
            ...item,
            id: idx + 1,
            value: item.recipientId as string,
            isRead: String(item.isRead),
          }));
          setStickyDataSource(items);
          setFetchError(null);
        } else {
          setFetchError("Failed to load notifications.");
        }
      } catch (e) {
        logger.error("Notifications error in NotificationData:", e);
        if (!cancelled) {
          setFetchError("Failed to load notifications.");
        }
      }
    };

    loadNotifications();

    return () => {
      cancelled = true;
    };
  }, [env, initialDays]);

  return (
    <>
      {fetchError && (
        <div style={{ color: "red", margin: 10 }}>{fetchError}</div>
      )}
      <StickyHeaderExample data={stickyDataSource} pageSize={pageSize} />
    </>
  );
}

const ContentArea = styled.div`
  ${({ theme }) => css`
    color: ${theme.HarmonyBodyColorDefault};
    font-family: ${theme.HarmonySemanticFontFamilyAction};
    font-weight: ${theme.HarmonySemanticFontWeightRegular};
    font-size: ${theme.HarmonySemanticFontSizeBase};
    line-height: ${theme.HarmonySemanticLineHeightBody};
    text-decoration: none;
  `}
`;

const HeaderText = styled.div`
  white-space: nowrap;
`;

function getExpandableContentProp(data: NotificationItem[]) {
  return {
    getExpandButtonAriaLabel: (rowData: NotificationItem) =>
      rowData.message ?? "",
    getHideRowButtonAriaLabel: (rowData: NotificationItem) =>
      rowData.message ?? "",
    rows: data.reduce<Record<number, { content: React.ReactNode }>>(
      (acc, row) => {
        acc[row.id] = {
          content: (
            <ContentArea>
              <pre
                style={{
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-all",
                  margin: 0,
                }}
              >
                {JSON.stringify(row, null, 2)}
              </pre>
            </ContentArea>
          ),
        };
        return acc;
      },
      {},
    ),
  };
}

const getSortCaptionDescription = (
  sortColumnLabel: string,
  isSortAscending: boolean,
) =>
  isSortAscending
    ? `Column ${sortColumnLabel} is sorted in ascending order`
    : `Column ${sortColumnLabel} is sorted in descending order`;

const stickyConfig = {
  columns: [
    { label: "Id", key: "id", sortColumn: false },
    { label: "Created At", key: "createdAt" },
    { label: "Title", key: "title", sortColumn: false },
    { label: "Priority", key: "priority" },
    { label: "Category", key: "category" },
    { label: "IsRead", key: "isRead" },
  ],
  caption: {
    headerText: <HeaderText>Last 25 Notifications in details</HeaderText>,
    descriptionText: "Click a column header to sort.",
    getSortCaptionDescription,
  },
  sticky: {
    headerSticky: true,
    maxHeight: "100%",
  },
  sort: true,
  onSort: null,
};

interface StickyHeaderExampleProps {
  data?: NotificationItem[];
  config?: typeof stickyConfig;
  className?: string;
  pageSize: number;
}

const StickyHeaderExample = ({
  data = [],
  config = stickyConfig,
  className = "",
  pageSize,
}: StickyHeaderExampleProps) => {
  const [page, setPage] = useState(1);
  const totalPages = Math.ceil((data.length || 1) / pageSize);

  const goToPreviousPage = () =>
    setPage((currentPage) => Math.max(1, currentPage - 1));
  const goToNextPage = () =>
    setPage((currentPage) => Math.min(totalPages, currentPage + 1));
  const navigate = useNavigate();
  const expandableContent = getExpandableContentProp(data);
  const handleSort = () => setPage(1);

  return (
    <div style={{ marginLeft: 20, marginRight: 20 }} className={className}>
      <div style={{ marginTop: 20 }}>
        <div className="row pb-base">
          <div style={{ marginRight: 40 }}>
            <Button variant="secondary" size="m" onPress={() => navigate(-1)}>
              Back
            </Button>
          </div>
        </div>
      </div>

      <Table
        id="team-directory-table"
        data={data}
        config={config}
        page={page}
        pageSize={pageSize}
        expandableContent={expandableContent}
        onSort={handleSort}
      />

      <div style={{ marginTop: 10 }} className="row">
        <Text weight="bold">
          Page {page} of {totalPages}
        </Text>
      </div>
      <div style={{ marginTop: 20 }}>
        <div className="row pb-base">
          <Button className="mr-s" onPress={goToPreviousPage}>
            Previous
          </Button>
          <Button onPress={goToNextPage}>Next</Button>
        </div>
      </div>
    </div>
  );
};

export { NotificationData };
